import {
  AlertTriangle,
  CheckCircle2,
  FileSearch,
  Loader2,
  Plus,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useM3RigheMerce, useM3RileggiRigheMerce, useM3SalvaRigheMerce } from "@/hooks/useM3";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { RigaMerce } from "@/types/api";

/**
 * L'elenco della merce che parte: quello che finisce sulla packing list e sui
 * certificati. Si corregge qui — celle scrivibili in tabella, come il tabellone
 * di Marina — perché una lettura sbagliata della nota non deve poter uscire dal
 * portale senza che nessuno possa rimediare (difetto NDP 1088, 29-08-2026).
 *
 * Il confronto col piede della nota resta sempre in vista: non è un blocco
 * muto, dice *dove* non torna e quanto manca.
 */

type Bozza = {
  key: string;
  codice_articolo: string;
  // Il codice che va sui documenti al posto di quello del gestionale.
  codice_articolo_alt: string;
  descrizione: string;
  lotto: string;
  scadenza: string;
  colli: string;
  pallet: string;
  pezzi_per_pallet: string;
  // Non si mostrano, ma il salvataggio riscrive l'elenco intero: se non
  // viaggiassero con la riga, ogni correzione li cancellerebbe.
  ordine_riferimento: string | null;
  a_terra: number | null;
  fonte?: RigaMerce["fonte"];
};

const nuovaRiga = (): Bozza => ({
  key: `nuova-${Math.random().toString(36).slice(2)}`,
  codice_articolo: "",
  codice_articolo_alt: "",
  descrizione: "",
  lotto: "",
  scadenza: "",
  colli: "",
  pallet: "",
  pezzi_per_pallet: "",
  ordine_riferimento: null,
  a_terra: null,
});

const daRiga = (r: RigaMerce): Bozza => ({
  key: r.id,
  codice_articolo: r.codice_articolo,
  codice_articolo_alt: r.codice_articolo_alt ?? "",
  descrizione: r.descrizione ?? "",
  lotto: r.lotto ?? "",
  scadenza: r.scadenza ?? "",
  colli: String(r.colli ?? ""),
  pallet: String(r.pallet ?? ""),
  pezzi_per_pallet: r.pezzi_per_pallet != null ? String(r.pezzi_per_pallet) : "",
  ordine_riferimento: r.ordine_riferimento,
  a_terra: r.a_terra,
  fonte: r.fonte,
});

const num = (v: string) => {
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const fmt = (n: number) => new Intl.NumberFormat("it-IT").format(n);

function Cella({
  value,
  onChange,
  className,
  type = "text",
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  type?: string;
  placeholder?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "w-full rounded border border-transparent bg-transparent px-1.5 py-1 text-[12.5px]",
        "hover:border-border focus:border-brand-400 focus:bg-background focus:outline-none",
        className,
      )}
    />
  );
}

export function RigheMerce({ caricoId }: { caricoId: string }) {
  const { data, isLoading } = useM3RigheMerce(caricoId);
  const salva = useM3SalvaRigheMerce(caricoId);
  const rileggi = useM3RileggiRigheMerce(caricoId);
  const [bozze, setBozze] = useState<Bozza[] | null>(null);
  const [erroreRilettura, setErroreRilettura] = useState<string | null>(null);

  useEffect(() => {
    if (data) setBozze(data.righe.map(daRiga));
  }, [data]);

  const righe = useMemo(() => bozze ?? [], [bozze]);
  const totali = useMemo(
    () => ({
      colli: righe.reduce((s, r) => s + num(r.colli), 0),
      pallet: righe.reduce((s, r) => s + num(r.pallet), 0),
    }),
    [righe],
  );

  if (isLoading || !data || !bozze) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const sporco =
    JSON.stringify(data.righe.map(daRiga).map(({ key: _k, ...r }) => r)) !==
    JSON.stringify(righe.map(({ key: _k, ...r }) => r));

  // Quadratura ricalcolata mentre si scrive: il numero deve rientrare sotto gli
  // occhi di chi corregge, non dopo il salvataggio.
  const scartoColli = data.colli_nota != null ? totali.colli - data.colli_nota : null;
  const scartoPallet = data.pallet_nota != null ? totali.pallet - data.pallet_nota : null;
  const quadra = !scartoColli && !scartoPallet && data.colli_nota != null;

  const aggiorna = (key: string, campo: keyof Bozza, valore: string) =>
    setBozze((b) => (b ?? []).map((r) => (r.key === key ? { ...r, [campo]: valore } : r)));

  const salvaRighe = () =>
    salva.mutate(
      righe
        .filter((r) => r.codice_articolo.trim())
        .map((r) => ({
          codice_articolo: r.codice_articolo.trim(),
          codice_articolo_alt: r.codice_articolo_alt.trim() || null,
          descrizione: r.descrizione.trim() || null,
          ordine_riferimento: r.ordine_riferimento,
          lotto: r.lotto.trim() || null,
          scadenza: r.scadenza || null,
          pezzi_per_pallet: r.pezzi_per_pallet ? num(r.pezzi_per_pallet) : null,
          colli: num(r.colli),
          pallet: num(r.pallet),
          a_terra: r.a_terra,
        })),
    );

  return (
    <section>
      <div className="mb-2.5 flex items-center gap-2">
        <h4 className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
          Merce del carico · {righe.length} righe
        </h4>
        {data.ratificata && (
          <span
            title={`Confermate da ${data.ratificata_da ?? "un operatore"}${
              data.ratificata_il ? ` il ${new Date(data.ratificata_il).toLocaleDateString("it-IT")}` : ""
            }`}
            className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"
          >
            confermate{data.ratificata_da ? ` · ${data.ratificata_da.split(" ")[0]}` : ""}
          </span>
        )}
      </div>

      {/* Semaforo: righe contro il piede della nota di prelievo */}
      <div
        className={cn(
          "mb-2.5 flex items-start gap-2 rounded-lg border p-2.5 text-[12px]",
          quadra
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
            : "border-amber-300 bg-amber-50 text-amber-900",
        )}
      >
        {quadra ? (
          <CheckCircle2 className="mt-px h-4 w-4 flex-none" />
        ) : (
          <AlertTriangle className="mt-px h-4 w-4 flex-none" />
        )}
        <div className="min-w-0">
          {data.colli_nota == null ? (
            <>La nota non riporta i totali: le righe non sono verificabili automaticamente.</>
          ) : quadra ? (
            <>
              Le righe tornano con la nota {data.numero_ndp ? `n. ${data.numero_ndp}` : ""}:{" "}
              <b>{fmt(totali.pallet)} pallet</b>.
            </>
          ) : (
            <>
              Le righe fanno <b>{fmt(totali.colli)} colli</b> e <b>{fmt(totali.pallet)} pallet</b>,
              la nota {data.numero_ndp ? `n. ${data.numero_ndp} ` : ""}dice{" "}
              <b>{fmt(data.colli_nota)}</b> e <b>{fmt(data.pallet_nota ?? 0)}</b>
              {scartoColli ? ` (${scartoColli > 0 ? "+" : ""}${fmt(scartoColli)} colli` : " ("}
              {scartoPallet ? `, ${scartoPallet > 0 ? "+" : ""}${fmt(scartoPallet)} pallet` : ""}
              ).{" "}
              {data.ratificata
                ? "Le hai confermate tu, quindi il plico si genera lo stesso."
                : "Finché non tornano — o non confermi tu — il plico non si genera."}
            </>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[780px] table-fixed text-left">
          {/* La descrizione è ciò con cui si riconosce l'articolo: è la colonna
              che merita lo spazio, i numeri stanno stretti. */}
          <colgroup>
            <col className="w-[13%]" />
            <col className="w-[12%]" />
            <col className="w-[28%]" />
            <col className="w-[13%]" />
            <col className="w-[13%]" />
            <col className="w-[9%]" />
            <col className="w-[8%]" />
            <col className="w-[4%]" />
          </colgroup>
          <thead className="bg-muted/60 text-[10.5px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 font-semibold">Articolo</th>
              <th
                className="px-2 py-1.5 font-semibold"
                title="Il codice alternativo della nota: è quello che va sui documenti. Se manca, si stampa il codice del gestionale."
              >
                Cod. sui doc.
              </th>
              <th className="px-2 py-1.5 font-semibold">Descrizione</th>
              <th className="px-2 py-1.5 font-semibold">Lotto</th>
              <th className="px-2 py-1.5 font-semibold">Scadenza</th>
              <th className="px-2 py-1.5 text-right font-semibold">Colli</th>
              <th className="px-2 py-1.5 text-right font-semibold">Pallet</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {righe.map((r) => (
              <tr key={r.key} className="hover:bg-muted/30">
                <td className="px-1 py-0.5">
                  <Cella
                    value={r.codice_articolo}
                    onChange={(v) => aggiorna(r.key, "codice_articolo", v)}
                    className="font-semibold"
                    placeholder="codice"
                  />
                </td>
                <td className="px-1 py-0.5">
                  <Cella
                    value={r.codice_articolo_alt}
                    onChange={(v) => aggiorna(r.key, "codice_articolo_alt", v)}
                    placeholder={r.codice_articolo || "alternativo"}
                    className="placeholder:italic placeholder:text-muted-foreground/60"
                  />
                </td>
                <td className="px-1 py-0.5">
                  <Cella
                    value={r.descrizione}
                    onChange={(v) => aggiorna(r.key, "descrizione", v)}
                    className="text-muted-foreground"
                  />
                </td>
                <td className="px-1 py-0.5">
                  <Cella value={r.lotto} onChange={(v) => aggiorna(r.key, "lotto", v)} />
                </td>
                <td className="px-1 py-0.5">
                  <Cella
                    type="date"
                    value={r.scadenza}
                    onChange={(v) => aggiorna(r.key, "scadenza", v)}
                  />
                </td>
                <td className="px-1 py-0.5">
                  <Cella
                    value={r.colli}
                    onChange={(v) => aggiorna(r.key, "colli", v)}
                    className="text-right tabular-nums"
                  />
                </td>
                <td className="px-1 py-0.5">
                  <Cella
                    value={r.pallet}
                    onChange={(v) => aggiorna(r.key, "pallet", v)}
                    className="text-right tabular-nums"
                  />
                </td>
                <td className="px-1 py-0.5">
                  <button
                    title="Togli la riga"
                    onClick={() => setBozze((b) => (b ?? []).filter((x) => x.key !== r.key))}
                    className="rounded p-1 text-muted-foreground hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {righe.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-[12.5px] text-muted-foreground">
                  Nessuna riga. Rileggi la nota di prelievo per prenderle dal
                  documento, oppure aggiungile a mano.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="border-t border-border bg-muted/40 text-[12.5px] font-semibold">
            <tr>
              {/* Solo i pallet (Enrico, 03-10-2026): la somma delle quantità mescola
                  scatole e sacchi e non dice niente. I colli restano nel confronto
                  con la nota qui sopra, che è dove servono. */}
              <td className="px-2 py-1.5" colSpan={6}>
                Totale pallet
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmt(totali.pallet)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-2.5 flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setBozze((b) => [...(b ?? []), nuovaRiga()])}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Aggiungi riga
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={rileggi.isPending}
          title="Rilegge il PDF della nota e rifà le righe (le correzioni a mano si perdono)"
          onClick={() => {
            setErroreRilettura(null);
            rileggi.mutate(undefined, {
              onError: (err) =>
                setErroreRilettura(
                  err instanceof ApiError && typeof err.body === "object" && err.body
                    ? String((err.body as { detail?: string }).detail ?? err.message)
                    : "Non sono riuscito a rileggere la nota.",
                ),
            });
          }}
        >
          {rileggi.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileSearch className="mr-1.5 h-3.5 w-3.5" />
          )}
          Rileggi la nota
        </Button>
        {sporco && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setBozze(data.righe.map(daRiga))}
            className="text-muted-foreground"
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Annulla modifiche
          </Button>
        )}
        <div className="flex-1" />
        <Button size="sm" onClick={salvaRighe} disabled={salva.isPending}>
          {salva.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Rigenero il pacchetto…
            </>
          ) : sporco ? (
            "Salva e rigenera il pacchetto"
          ) : (
            "Conferma le righe"
          )}
        </Button>
      </div>
      {erroreRilettura && (
        <p className="mt-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11.5px] text-amber-900">
          {erroreRilettura}
        </p>
      )}
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        Salvando, tutti i documenti del pacchetto vengono rigenerati con questi numeri e resta
        scritto chi ha corretto.
      </p>
    </section>
  );
}
